/** @jsxRuntime classic*/
/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { FC, Fragment, useMemo } from 'react';
import { Modal } from '../../../components/Modal/modal';
import { ThreeRowModalLayout } from '../../../molecules/layouts';
import { Tabs } from '../../../molecules/tabs';
import { useDiscountModal } from '../hooks/useDiscountModal';
import { DiscountModalProps, DiscountModalTabEnum } from '../types';
import { getActiveState } from '../utils';
import { CustomDiscount } from './discount-custom';
import { DiscountFooter } from './discount-footer';
import { DiscountHeader } from './discount-header';
import { PromoCodeTab } from './promo-code/promo-code-tab';

export const DiscountModal: FC<DiscountModalProps> = ({
	totalAmount,
	subtotalBalance,
	discountOn,
	reasons,
	isOpen,
	toggle,
	onSubmit,
	isAppliedPromoCode,
	withTabs = true,
	fetchData,
	isApplyProcessing,
	discountReasonsError,
	variant,
}) => {
	const {
		handleOnChangeCustom,
		handleOnChangePromoCode,
		isValid,
		currentTab,
		customState,
		promoCodeState,
		remainingAmount,
		remainingPercentage,
		tabs,
		handleTabChange,
		resetModal,
	} = useDiscountModal(withTabs, totalAmount, subtotalBalance);
	const mappedReasons = reasons?.map(reason => ({
		value: reason.id,
		label: reason.reason,
		key: `option-${reason.ordinal}`,
	}));

	const handleSubmit = () => {
		const activeState = getActiveState(customState, promoCodeState, currentTab);
		onSubmit(activeState, resetModal);
	};

	const handleOnClose = () => {
		toggle();
		resetModal();
	};

	const tabsComponents = useMemo(
		() => [
			{
				id: DiscountModalTabEnum.PROMO_CODE,
				component: fetchData ? (
					<PromoCodeTab
						key={DiscountModalTabEnum.PROMO_CODE}
						onChange={handleOnChangePromoCode}
						initialState={promoCodeState}
						fetchData={fetchData}
						isAppliedPromoCode={isAppliedPromoCode}
						variant={variant}
					/>
				) : (
					<Fragment />
				),
			},
			{
				id: DiscountModalTabEnum.CUSTOM,
				component: (
					<CustomDiscount
						key={DiscountModalTabEnum.CUSTOM}
						totalAmount={totalAmount}
						remainingAmount={remainingAmount}
						remainingPercentage={remainingPercentage}
						reasons={mappedReasons}
						onChange={handleOnChangeCustom}
						discountApplicant={discountOn}
						initialState={customState}
						discountReasonsError={discountReasonsError}
						variant={variant}
					/>
				),
			},
		],
		[totalAmount, remainingAmount, remainingPercentage, mappedReasons, promoCodeState, customState, variant]
	);

	const activeTabComponent = tabsComponents.find(tab => tab.id === currentTab)?.component;

	return (
		<Modal isShowing={isOpen} toggle={handleOnClose} padding={0} minWidth={'480px'} maxWidth={'480px'} isDismissable>
			<ThreeRowModalLayout
				headerSeparator
				footerSeparator
				iswithTabs={withTabs}
				header={
					<DiscountHeader
						variant={variant}
						totalAmount={totalAmount}
						subtotalBalance={subtotalBalance}
						type={discountOn}
						tabs={
							withTabs && currentTab ? (
								<Tabs tabs={tabs} currentTab={currentTab} handleTabSelect={handleTabChange} fullWidthSpread />
							) : (
								<Fragment />
							)
						}
					/>
				}
				content={
					withTabs ? (
						activeTabComponent
					) : (
						<CustomDiscount
							totalAmount={subtotalBalance ?? totalAmount}
							remainingAmount={remainingAmount}
							remainingPercentage={remainingPercentage}
							reasons={mappedReasons}
							onChange={handleOnChangeCustom}
							discountApplicant={discountOn}
							variant={variant}
						/>
					)
				}
				contentMinHeight={'280px'}
				footer={
					<DiscountFooter
						isValid={isValid}
						onCancel={handleOnClose}
						onSubmit={handleSubmit}
						isProcessing={isApplyProcessing}
					/>
				}
			/>
		</Modal>
	);
};
